import { MOOD_PALETTES } from '../components/OrbBall/OrbBall';
import { GAME_LOGO, BG_MOBILE } from './assets';

let logoPromise = null;
let backgroundPromise = null;

/** Loads the mobile background once; resolves to null if it can't be fetched. */
export function loadBackground() {
  if (!backgroundPromise) {
    backgroundPromise = new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = BG_MOBILE;
    });
  }
  return backgroundPromise;
}

const CARD_FONT = 'Mirza, Georgia, serif';

/** Draws `img` scaled to cover the whole canvas, cropped around the centre. */
function drawCover(ctx, img, W, H) {
  const scale = Math.max(W / img.width, H / img.height);
  const w = img.width * scale;
  const h = img.height * scale;
  ctx.drawImage(img, (W - w) / 2, (H - h) / 2, w, h);
}

/** Loads the game logo once; resolves to null if it can't be fetched. */
export function loadLogo() {
  if (!logoPromise) {
    logoPromise = new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = GAME_LOGO;
    });
  }
  return logoPromise;
}

export const CARD_FORMATS = {
  story: { key: 'story', label: 'Story', width: 1080, height: 1920, hint: '1080 × 1920' },
  square: { key: 'square', label: 'Square', width: 1080, height: 1080, hint: '1080 × 1080' },
};

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Wraps `text` to `maxWidth`, returning the lines. Breaks over-long words. */
function wrapText(ctx, text, maxWidth) {
  const words = String(text).split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';

  const pushBrokenWord = (word) => {
    let chunk = '';
    for (const ch of word) {
      if (ctx.measureText(chunk + ch).width > maxWidth && chunk) {
        lines.push(chunk);
        chunk = ch;
      } else {
        chunk += ch;
      }
    }
    line = chunk;
  };

  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth) {
      line = candidate;
      continue;
    }
    if (line) { lines.push(line); line = ''; }
    if (ctx.measureText(word).width > maxWidth) pushBrokenWord(word);
    else line = word;
  }
  if (line) lines.push(line);
  return lines.length ? lines : [''];
}

function drawCenteredLines(ctx, lines, centerX, startY, lineHeight) {
  lines.forEach((line, i) => ctx.fillText(line, centerX, startY + i * lineHeight));
  return startY + lines.length * lineHeight;
}

function drawOrb(ctx, cx, cy, radius, palette) {
  const glow = ctx.createRadialGradient(cx, cy, radius * 0.2, cx, cy, radius * 1.8);
  glow.addColorStop(0, palette.glow);
  glow.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(cx, cy, radius * 1.8, 0, Math.PI * 2);
  ctx.fill();

  const body = ctx.createRadialGradient(
    cx - radius * 0.3, cy - radius * 0.35, radius * 0.1,
    cx, cy, radius
  );
  body.addColorStop(0, palette.core);
  body.addColorStop(0.55, palette.mid);
  body.addColorStop(1, palette.edge);
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = 'rgba(255,255,255,0.45)';
  ctx.beginPath();
  ctx.ellipse(cx - radius * 0.32, cy - radius * 0.38, radius * 0.24, radius * 0.15, -0.5, 0, Math.PI * 2);
  ctx.fill();
}

function starPath(ctx, cx, cy, r) {
  ctx.beginPath();
  ctx.moveTo(cx, cy - r);
  ctx.quadraticCurveTo(cx + r * 0.16, cy - r * 0.16, cx + r, cy);
  ctx.quadraticCurveTo(cx + r * 0.16, cy + r * 0.16, cx, cy + r);
  ctx.quadraticCurveTo(cx - r * 0.16, cy + r * 0.16, cx - r, cy);
  ctx.quadraticCurveTo(cx - r * 0.16, cy - r * 0.16, cx, cy - r);
  ctx.closePath();
}

/** A small corner flourish, drawn from the corner inwards. */
function drawFlourish(ctx, x, y, size, flipX, flipY) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(flipX, flipY);
  ctx.beginPath();
  ctx.moveTo(0, size);
  ctx.quadraticCurveTo(0, 0, size, 0);
  ctx.moveTo(size * 0.26, size * 0.62);
  ctx.quadraticCurveTo(size * 0.26, size * 0.26, size * 0.62, size * 0.26);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(size * 0.34, size * 0.34, size * 0.05, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/**
 * Faint background decoration: haloed rings behind the masthead, drifting
 * stars, corner flourishes and soft bokeh. All very low contrast — it should
 * read as texture, never compete with the prophecy.
 */
function drawBackdrop(ctx, W, H, palette, isStory) {
  const gold = 'rgba(194, 150, 63, ';

  // Concentric rings, centred where the orb/masthead sits.
  const ringCx = W / 2;
  const ringCy = H * (isStory ? 0.3 : 0.32);
  [0.32, 0.45, 0.6, 0.78].forEach((f, i) => {
    ctx.strokeStyle = `${gold}${0.13 - i * 0.02})`;
    ctx.lineWidth = i === 1 ? 3 : 1.5;
    ctx.beginPath();
    ctx.arc(ringCx, ringCy, W * f, 0, Math.PI * 2);
    ctx.stroke();
  });

  // Dotted ring, like an astrolabe scale.
  ctx.fillStyle = `${gold}0.16)`;
  const dots = 48;
  for (let i = 0; i < dots; i++) {
    const a = (i / dots) * Math.PI * 2;
    const rr = W * 0.52;
    ctx.beginPath();
    ctx.arc(ringCx + Math.cos(a) * rr, ringCy + Math.sin(a) * rr, i % 4 === 0 ? 5 : 2.5, 0, Math.PI * 2);
    ctx.fill();
  }

  // Bokeh: a few soft mood-tinted blooms.
  const blooms = [
    [0.14, 0.14, 0.22], [0.88, 0.2, 0.16], [0.08, 0.62, 0.18],
    [0.93, 0.7, 0.2], [0.5, 0.92, 0.26],
  ];
  blooms.forEach(([fx, fy, fr]) => {
    const r = W * fr;
    const g = ctx.createRadialGradient(W * fx, H * fy, 0, W * fx, H * fy, r);
    g.addColorStop(0, palette.glow);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.globalAlpha = 0.14;
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(W * fx, H * fy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  });

  // Scattered four-point stars — fixed positions so the card is reproducible.
  const stars = [
    [0.12, 0.08, 16], [0.86, 0.11, 22], [0.22, 0.24, 11], [0.78, 0.3, 13],
    [0.09, 0.44, 18], [0.91, 0.49, 12], [0.16, 0.7, 14], [0.84, 0.76, 19],
    [0.3, 0.86, 11], [0.68, 0.9, 15], [0.5, 0.06, 13], [0.42, 0.75, 9],
  ];
  stars.forEach(([fx, fy, size], i) => {
    ctx.fillStyle = i % 3 === 0 ? `${gold}0.32)` : 'rgba(109, 16, 48, 0.14)';
    starPath(ctx, W * fx, H * fy, size);
    ctx.fill();
  });

  // Corner flourishes just inside the gold frame.
  ctx.strokeStyle = `${gold}0.38)`;
  ctx.fillStyle = `${gold}0.38)`;
  ctx.lineWidth = 3;
  const inset = 72;
  const fs = isStory ? 120 : 96;
  drawFlourish(ctx, inset, inset, fs, 1, 1);
  drawFlourish(ctx, W - inset, inset, fs, -1, 1);
  drawFlourish(ctx, inset, H - inset, fs, 1, -1);
  drawFlourish(ctx, W - inset, H - inset, fs, -1, -1);
}

/**
 * Draws the shareable prophecy card onto `canvas`.
 * Everything the PRD asks for: question, orb response, mood, desperation
 * label and ExtraMilePlay branding over a mood-tinted mystical background.
 */
export function drawProphecyCard(canvas, { question, response, mood, desperation, format = 'story', logo = null, background = null }) {
  const spec = CARD_FORMATS[format] || CARD_FORMATS.story;
  const { width: W, height: H } = spec;
  canvas.width = W;
  canvas.height = H;

  const ctx = canvas.getContext('2d');
  const palette = MOOD_PALETTES[mood] || MOOD_PALETTES.default;
  const isStory = spec.key === 'story';

  // Background ------------------------------------------------------------
  if (background) {
    drawCover(ctx, background, W, H);
  } else {
    const bg = ctx.createLinearGradient(0, 0, W, H);
    bg.addColorStop(0, '#fbf4ec');
    bg.addColorStop(1, '#f2e2d2');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);
  }

  const tint = ctx.createRadialGradient(W * 0.5, H * 0.32, 0, W * 0.5, H * 0.32, W * 0.9);
  tint.addColorStop(0, palette.glow);
  tint.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.globalAlpha = 0.35;
  ctx.fillStyle = tint;
  ctx.fillRect(0, 0, W, H);
  ctx.globalAlpha = 1;

  if (!background) drawBackdrop(ctx, W, H, palette, isStory);

  // Gold frame ------------------------------------------------------------
  ctx.strokeStyle = 'rgba(194, 150, 63, 0.55)';
  ctx.lineWidth = 4;
  roundRect(ctx, 40, 40, W - 80, H - 80, 36);
  ctx.stroke();

  ctx.textAlign = 'center';

  const topY = isStory ? 150 : 110;
  const brandingY = H - (isStory ? 110 : 90);
  const moodLine = desperation ? `Mood: ${mood}  ·  ${desperation}` : `Mood: ${mood}`;

  // The body is measured before it is drawn and shrunk until it fits between
  // the masthead and the branding — a 140-character question paired with a
  // long prophecy overflows the card at full size otherwise.
  const base = {
    label: isStory ? 34 : 28,
    question: isStory ? 46 : 38,
    questionLead: isStory ? 60 : 50,
    answer: isStory ? 62 : 50,
    answerLead: isStory ? 82 : 66,
    mood: isStory ? 36 : 30,
    gapLabel: isStory ? 64 : 52,
    gapBlock: isStory ? 60 : 44,
    gapMood: isStory ? 70 : 54,
    boxPad: isStory ? 80 : 64,
    logoW: isStory ? 640 : 520,
  };

  const measure = (scale) => {
    const f = (n) => Math.round(n * scale);
    const logoH = logo ? (base.logoW * scale) * (logo.height / logo.width) : 0;
    const mastheadHeight = logo
      ? logoH + f(isStory ? 140 : 90)
      : f(isStory ? 600 : 400);
    const mastheadBottom = topY + mastheadHeight;

    ctx.font = `400 ${f(base.question)}px ${CARD_FONT}`;
    const questionLines = wrapText(ctx, question, W - 260);
    ctx.font = `700 ${f(base.answer)}px ${CARD_FONT}`;
    const answerLines = wrapText(ctx, response, W - 220);

    const bodyHeight =
      f(base.gapLabel) + questionLines.length * f(base.questionLead) +
      f(base.gapBlock) + f(isStory ? 40 : 30) +
      answerLines.length * f(base.answerLead) + f(base.boxPad) +
      f(base.gapMood) + f(base.mood);

    return { f, logoH, mastheadHeight, mastheadBottom, questionLines, answerLines, bodyHeight };
  };

  let layout = measure(1);
  for (let scale = 1; scale >= 0.55; scale -= 0.05) {
    layout = measure(scale);
    if (layout.mastheadBottom + layout.bodyHeight <= brandingY - 40) break;
  }
  const { f, logoH, mastheadHeight, questionLines, answerLines } = layout;

  // Center the whole composition in the space above the branding, so a short
  // prophecy doesn't leave the bottom half of the card empty.
  const available = brandingY - 40 - topY;
  const composition = mastheadHeight + layout.bodyHeight;
  const startY = topY + Math.max(0, (available - composition) / 2);

  // Masthead --------------------------------------------------------------
  // The logo artwork already contains an orb, so the drawn orb below is only
  // the fallback anchor for when the image didn't load.
  let y = startY + mastheadHeight;
  if (logo) {
    const logoW = logoH * (logo.width / logo.height);
    ctx.drawImage(logo, (W - logoW) / 2, startY, logoW, logoH);
  } else {
    ctx.fillStyle = '#6d1030';
    ctx.font = `700 ${f(isStory ? 86 : 70)}px ${CARD_FONT}`;
    ctx.fillText('ASK THE ORB', W / 2, startY + f(60));
    drawOrb(ctx, W / 2, startY + f(isStory ? 380 : 250), f(isStory ? 190 : 130), palette);
  }

  // Question --------------------------------------------------------------
  ctx.fillStyle = 'rgba(122, 85, 101, 0.95)';
  ctx.font = `500 ${f(base.label)}px ${CARD_FONT}`;
  ctx.fillText('QUESTION', W / 2, y);

  y += f(base.gapLabel);
  ctx.fillStyle = '#3b1425';
  ctx.font = `400 ${f(base.question)}px ${CARD_FONT}`;
  y = drawCenteredLines(ctx, questionLines, W / 2, y, f(base.questionLead));

  // Answer ----------------------------------------------------------------
  y += f(base.gapBlock);
  ctx.fillStyle = 'rgba(122, 85, 101, 0.95)';
  ctx.font = `500 ${f(base.label)}px ${CARD_FONT}`;
  ctx.fillText('THE ORB SAYS', W / 2, y);

  y += f(isStory ? 40 : 30);
  const answerHeight = answerLines.length * f(base.answerLead) + f(base.boxPad);

  ctx.fillStyle = 'rgba(255, 255, 255, 0.72)';
  roundRect(ctx, 100, y, W - 200, answerHeight, 28);
  ctx.fill();
  ctx.strokeStyle = 'rgba(109, 16, 48, 0.18)';
  ctx.lineWidth = 2;
  roundRect(ctx, 100, y, W - 200, answerHeight, 28);
  ctx.stroke();

  ctx.fillStyle = '#6d1030';
  ctx.font = `700 ${f(base.answer)}px ${CARD_FONT}`;
  drawCenteredLines(ctx, answerLines, W / 2, y + f(base.boxPad) + f(base.answerLead) * 0.25, f(base.answerLead));
  y += answerHeight;

  // Mood + desperation ----------------------------------------------------
  y += f(base.gapMood);
  ctx.fillStyle = 'rgba(122, 85, 101, 0.95)';
  ctx.font = `500 ${f(base.mood)}px ${CARD_FONT}`;
  ctx.fillText(moodLine, W / 2, y);

  // Branding --------------------------------------------------------------
  ctx.fillStyle = 'rgba(109, 16, 48, 0.75)';
  ctx.font = `700 ${isStory ? 38 : 32}px ${CARD_FONT}`;
  ctx.fillText('ExtraMilePlay', W / 2, brandingY);

  return canvas;
}

export function canvasToJpegDataUrl(canvas) {
  return canvas.toDataURL('image/jpeg', 0.92);
}

export function canvasToJpegBlob(canvas) {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
}
